import React, { createContext, useContext, useState, ReactNode } from "react";

interface LocalizacaoAberta {
  ean: string;
  nome: string;
  tipo: string;
  timestamp: number;
}

interface MovimentacaoContextType {
  localizacaoAberta: boolean;
  setLocalizacaoAberta: (aberta: boolean) => void;
  tipoMovimentacaoAtual: string | null;
  setTipoMovimentacaoAtual: (tipo: string | null) => void;
  // 🚨 NOVOS CAMPOS PARA RASTREAMENTO DE LOCALIZAÇÕES ABERTAS
  localizacoesAbertas: LocalizacaoAberta[];
  adicionarLocalizacaoAberta: (ean: string, nome: string, tipo: string) => void;
  removerLocalizacaoAberta: (ean: string) => void;
  limparTodasLocalizacoesAbertas: () => void;
  resetarMovimentacao: () => void;
}

const MovimentacaoContext = createContext<MovimentacaoContextType | undefined>(
  undefined,
);

export const useMovimentacao = () => {
  const context = useContext(MovimentacaoContext);
  if (context === undefined) {
    throw new Error(
      "useMovimentacao deve ser usado dentro de um MovimentacaoProvider",
    );
  }
  return context;
};

interface MovimentacaoProviderProps {
  children: ReactNode;
}

export const MovimentacaoProvider: React.FC<MovimentacaoProviderProps> = ({
  children,
}) => {
  const [localizacaoAberta, setLocalizacaoAberta] = useState(false);
  const [tipoMovimentacaoAtual, setTipoMovimentacaoAtual] = useState<
    string | null
  >(null);
  // 🚨 ESTADO PARA RASTREAMENTO DE LOCALIZAÇÕES ABERTAS
  const [localizacoesAbertas, setLocalizacoesAbertas] = useState<
    LocalizacaoAberta[]
  >([]);

  // 🚨 FUNÇÃO PARA ADICIONAR LOCALIZAÇÃO ABERTA
  const adicionarLocalizacaoAberta = (
    ean: string,
    nome: string,
    tipo: string,
  ) => {
    setLocalizacoesAbertas((prev) => {
      // Remove se já existir (evita duplicatas)
      const semDuplicatas = prev.filter((loc) => loc.ean !== ean);
      // Adiciona nova localização
      const novasLocalizacoes = [
        ...semDuplicatas,
        {
          ean,
          nome,
          tipo,
          timestamp: Date.now(),
        },
      ];

      // 🚨 SINCRONIZA COM LOCALSTORAGE PARA ACESSO DO AUTHCONTEXT
      try {
        localStorage.setItem(
          "localizacoesAbertas",
          JSON.stringify(novasLocalizacoes),
        );
      } catch (error) {
        console.warn("⚠️ Erro ao salvar localizações no localStorage:", error);
      }

      return novasLocalizacoes;
    });
    setLocalizacaoAberta(true);
  };

  // 🚨 FUNÇÃO PARA REMOVER LOCALIZAÇÃO ABERTA
  const removerLocalizacaoAberta = (ean: string) => {
    setLocalizacoesAbertas((prev) => {
      const novasLocalizacoes = prev.filter((loc) => loc.ean !== ean);

      // 🚨 SINCRONIZA COM LOCALSTORAGE
      try {
        localStorage.setItem(
          "localizacoesAbertas",
          JSON.stringify(novasLocalizacoes),
        );
      } catch (error) {
        console.warn("⚠️ Erro ao atualizar localStorage:", error);
      }

      // 🚨 CORREÇÃO: Verifica se não há mais localizações abertas APÓS a remoção
      if (novasLocalizacoes.length === 0) {
        setLocalizacaoAberta(false);
      }

      return novasLocalizacoes;
    });
  };

  // 🚨 FUNÇÃO PARA LIMPAR TODAS AS LOCALIZAÇÕES ABERTAS
  const limparTodasLocalizacoesAbertas = () => {
    setLocalizacoesAbertas([]);
    setLocalizacaoAberta(false);

    // 🚨 LIMPA LOCALSTORAGE
    try {
      localStorage.removeItem("localizacoesAbertas");
    } catch (error) {
      console.warn("⚠️ Erro ao limpar localStorage:", error);
    }
  };

  const resetarMovimentacao = () => {
    setLocalizacaoAberta(false);
    setTipoMovimentacaoAtual(null);
    // 🚨 LIMPA TAMBÉM AS LOCALIZAÇÕES ABERTAS
    limparTodasLocalizacoesAbertas();
  };

  const value = {
    localizacaoAberta,
    setLocalizacaoAberta,
    tipoMovimentacaoAtual,
    setTipoMovimentacaoAtual,
    // 🚨 EXPÕE AS NOVAS FUNÇÕES
    localizacoesAbertas,
    adicionarLocalizacaoAberta,
    removerLocalizacaoAberta,
    limparTodasLocalizacoesAbertas,
    resetarMovimentacao,
  };

  return (
    <MovimentacaoContext.Provider value={value}>
      {children}
    </MovimentacaoContext.Provider>
  );
};
